import React from 'react';
import { Star, Shield, CreditCard, Clock, XCircle, Gift } from 'lucide-react';
import { GOOGLE_PLACE_CONFIG } from '../config/googlePlaces';

// WhatsApp number for 24/7 support
const WHATSAPP_LINK = "https://wa.me/905352385797";

/**
 * TrustBar Component - Mobile trust indicators
 * Variants: 'compact' (vehicle step), 'full' (home page mobile)
 * Design: Subtle border emphasis, not aggressive green
 */
const TrustBar = ({ variant = 'compact', className = '' }) => {
  const trustItems = [
    { icon: XCircle, text: 'Free cancellation up to 24h', shortText: 'Free Cancel' },
    { icon: Gift, text: 'Complimentary meet & greet', shortText: 'Meet & Greet' },
    { icon: Shield, text: 'No hidden fees', shortText: 'No Fees' },
    { icon: CreditCard, text: 'Secure payment (PayPal / Card)', shortText: 'Secure Pay' },
  ];

  if (variant === 'compact') {
    return (
      <div className={`bg-slate-50 border border-slate-200 rounded-xl p-4 ${className}`} data-testid="trust-bar-compact">
        <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center text-sm">
          {trustItems.map((item, index) => (
            <div 
              key={index} 
              className="flex items-center gap-1.5 text-slate-700"
            >
              <item.icon className="h-4 w-4 flex-shrink-0 text-primary" />
              <span className="text-xs sm:text-sm whitespace-nowrap">{item.text}</span>
            </div>
          ))}
        </div>
        {/* Rating */}
        <div className="flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-slate-200">
          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          <span className="text-sm font-semibold text-slate-900">4.9/5</span>
          <span className="text-xs text-slate-600">Rated by international travelers</span>
        </div>
      </div>
    );
  }

  // Full variant for home page (mobile) - 3 badges in one row desktop, 2+1 mobile
  return (
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 ${className}`} data-testid="trust-bar-full">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {/* Google Reviews */}
        <a 
          href={GOOGLE_PLACE_CONFIG.viewReviewsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:bg-white/5 rounded-lg p-2 transition-colors"
          data-testid="trust-google-reviews-link"
        >
          <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
          </div>
          <div>
            <p className="text-xs font-semibold text-white">4.9/5</p>
            <p className="text-[10px] text-gray-400">Google Reviews</p>
          </div>
        </a>
        {/* Free Cancellation - Subtle border emphasis */}
        <div className="flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-2">
          <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
            <XCircle className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs font-semibold text-white">Free</p>
            <p className="text-[10px] text-emerald-300">Cancellation</p>
          </div>
        </div>
        {/* Complimentary Meet & Greet - Subtle border emphasis */}
        <div className="flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-2 col-span-2 sm:col-span-1 justify-center sm:justify-start">
          <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
            <Gift className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs font-semibold text-white">Complimentary</p>
            <p className="text-[10px] text-emerald-300">Meet & Greet</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustBar;
